import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

const EMPTY_FORM = { name: '', location: '' };

function HospitalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('authToken');
  const isAdmin = user?.role === 'admin';

  const [hospitals, setHospitals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Form state (shared for Add & Edit)
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = adding new
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── API helpers ─────────────────────────────────────────────────────────

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const fetchHospitals = useCallback(async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API_URL}/api/hospitals`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to load hospitals');
      setHospitals(await res.json());
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchHospitals(); }, [fetchHospitals]);

  // ── Form handlers ────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (hospital) => {
    setEditingId(hospital.id);
    setForm({ name: hospital.name, location: hospital.location });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim() || !form.location.trim()) {
      setFormError('Both hospital name and location are required.');
      return;
    }

    setIsSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/hospitals/${editingId}`
        : `${API_URL}/api/hospitals`;

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: form.name.trim(), location: form.location.trim() }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');

      closeForm();
      fetchHospitals();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete handlers ──────────────────────────────────────────────────────

  const confirmDelete = (id) => setDeletingId(id);
  const cancelDelete = () => setDeletingId(null);

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/hospitals/${deletingId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      setDeletingId(null);
      fetchHospitals();
    } catch (err) {
      setFetchError(err.message);
      setDeletingId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="home-shell" style={{ paddingTop: '90px' }}>

      {/* Header */}
      <header className="home-header">
        <div className="home-brand">
          <span
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', fontSize: '20px', marginRight: '4px' }}
            title="Back to Home"
          >
            ←
          </span>
          <span className="home-logo-icon">🏥</span>
          <span className="home-brand-name">Hospital Locations</span>
        </div>
        {isAdmin && (
          <button
            onClick={openAdd}
            style={{
              background: '#2563EB',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontFamily: 'Segoe UI, Inter, sans-serif',
            }}
          >
            + Add Hospital
          </button>
        )}
      </header>

      {/* Page title */}
      <div style={{ width: '100%', maxWidth: '800px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1A2340', margin: '0 0 6px 0' }}>
          Manage Hospital Locations
        </h1>
        <p style={{ fontSize: '15px', color: '#6B7280', margin: 0 }}>
          Add, edit, or remove hospital names and their locations.
        </p>
      </div>

      {/* Fetch error */}
      {fetchError && (
        <div style={styles.errorBox}>
          <span>⚠️</span> {fetchError}
          <button onClick={fetchHospitals} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div style={{ fontSize: '15px', color: '#6B7280', marginTop: '40px' }}>
          ⏳ Loading hospitals...
        </div>
      ) : (
        <div style={{ width: '100%', maxWidth: '800px' }}>

          {hospitals.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏥</div>
              <p style={{ fontSize: '16px', color: '#6B7280', margin: 0 }}>
                No hospitals added yet. Click <strong>+ Add Hospital</strong> to get started.
              </p>
            </div>
          ) : (
            <div style={styles.table}>
              {/* Table header */}
              <div style={styles.tableHeader}>
                <span style={{ flex: 2 }}>Hospital Name</span>
                <span style={{ flex: 3 }}>Location</span>
                {isAdmin && <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>}
              </div>

              {/* Table rows */}
              {hospitals.map((h, idx) => (
                <div
                  key={h.id}
                  style={{
                    ...styles.tableRow,
                    background: idx % 2 === 0 ? '#fff' : '#F9FAFB',
                  }}
                >
                  <span style={{ flex: 2, fontWeight: '600', color: '#1A2340' }}>
                    {h.name}
                  </span>
                  <span style={{ flex: 3, color: '#4B5563' }}>
                    📍 {h.location}
                  </span>
                  {isAdmin && (
                    <span style={{ flex: 1, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button onClick={() => openEdit(h)} style={styles.editBtn} title="Edit">
                        ✏️ Edit
                      </button>
                      <button onClick={() => confirmDelete(h.id)} style={styles.deleteBtn} title="Delete">
                        🗑️
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '16px' }}>
            {hospitals.length} hospital{hospitals.length !== 1 ? 's' : ''} registered
          </p>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {showForm && (
        <div style={styles.overlay} onClick={closeForm}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingId ? '✏️ Edit Hospital' : '🏥 Add New Hospital'}
              </h2>
              <button onClick={closeForm} style={styles.closeBtn} title="Close">✕</button>
            </div>

            {formError && (
              <div style={{ ...styles.errorBox, marginBottom: '16px' }}>
                <span>⚠️</span> {formError}
              </div>
            )}

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>🏥 Hospital Name *</label>
                <input
                  type="text"
                  placeholder="e.g. City General Hospital"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  style={styles.input}
                  disabled={isSaving}
                  autoFocus
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>📍 Location *</label>
                <input
                  type="text"
                  placeholder="e.g. 12 Main Street, Chennai"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  style={styles.input}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" onClick={closeForm} style={styles.cancelBtn} disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" style={styles.saveBtn} disabled={isSaving}>
                  {isSaving ? '⏳ Saving...' : editingId ? '💾 Update' : '➕ Add Hospital'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deletingId && (
        <div style={styles.overlay} onClick={cancelDelete}>
          <div style={{ ...styles.modal, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1A2340', margin: '0 0 8px' }}>
                Delete Hospital?
              </h2>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px' }}>
                This will permanently remove the hospital record. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={cancelDelete} style={styles.cancelBtn} disabled={isDeleting}>
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{
                    ...styles.saveBtn,
                    background: isDeleting ? '#FCA5A5' : '#DC2626',
                    boxShadow: 'none',
                  }}
                >
                  {isDeleting ? '⏳ Deleting...' : '🗑️ Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles object ─────────────────────────────────────────────────────────────

const styles = {
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#FFF5F5',
    border: '2px solid #FEB2B2',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#C53030',
    width: '100%',
    maxWidth: '800px',
    marginBottom: '16px',
    boxSizing: 'border-box',
  },
  retryBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: '1.5px solid #C53030',
    borderRadius: '8px',
    padding: '4px 12px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#C53030',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 24px',
    background: '#fff',
    borderRadius: '20px',
    border: '2px dashed #D1D5DB',
  },
  table: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #E5E7EB',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    background: '#1E40AF',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    fontSize: '14px',
    borderBottom: '1px solid #F3F4F6',
    transition: 'background 0.15s',
  },
  editBtn: {
    background: '#EFF6FF',
    border: '1.5px solid #BFDBFE',
    borderRadius: '8px',
    padding: '5px 12px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1E40AF',
    cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  deleteBtn: {
    background: '#FFF5F5',
    border: '1.5px solid #FEB2B2',
    borderRadius: '8px',
    padding: '5px 10px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '24px',
  },
  modal: {
    background: '#fff',
    borderRadius: '20px',
    padding: '32px',
    width: '100%',
    maxWidth: '520px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    position: 'relative',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#1A2340',
    margin: 0,
  },
  closeBtn: {
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '8px',
    width: '32px',
    height: '32px',
    fontSize: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    color: '#4B5563',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fieldLabel: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#374151',
  },
  input: {
    padding: '12px 14px',
    fontSize: '15px',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    fontFamily: 'Segoe UI, Inter, sans-serif',
    color: '#1A2340',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    width: '100%',
  },
  cancelBtn: {
    padding: '10px 20px',
    background: '#F3F4F6',
    border: '1.5px solid #D1D5DB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#4B5563',
    cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  saveBtn: {
    padding: '10px 24px',
    background: '#2563EB',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif',
    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
  },
};

export default HospitalsPage;
